/**
 * Opening the CounterIntelligence template-setup overlay.
 *
 * Every path that turns freshly-added items into templates — the setup wizard
 * (public/add-items.html), the Item Catalog's "Create templates from these
 * items?" offer, the Invoice Inbox's "Add Items Only" follow-up, and the
 * in-chat import in CounterIntelligenceDrawer — hands the batch to the SAME
 * conversational assistant, so a user who adds items later gets exactly the
 * flow a new user got during setup.
 *
 * The overlay itself (public/js/template-setup-chat.js) only mounts on
 * count.html, so "open CounterIntelligence" means: stash the handoff, then go
 * to the Count page, where the assistant picks it up and starts the
 * conversation with the templates it proposes.
 *
 * The stash is split from the navigation so callers can report a storage
 * failure to the user instead of navigating to a page that would do nothing.
 */

import {
  TEMPLATE_SETUP_HANDOFF_KEY,
  buildTemplateSetupHandoff,
  templateSetupActiveKey,
} from './ciItemImport.mjs';

// Where the overlay lives. `from` is only a breadcrumb for the Count page.
export const TEMPLATE_SETUP_PATH = '/count.html?view=overview';

export const templateSetupHref = (from) => (
  from ? `${TEMPLATE_SETUP_PATH}&from=${encodeURIComponent(from)}` : TEMPLATE_SETUP_PATH
);

/**
 * Write the handoff the overlay reads on the Count page. Returns the stashed
 * handoff, or null when there is nothing to hand off (no store / no items) or
 * storage refused the write (private mode, quota).
 */
export const stashTemplateSetupHandoff = ({ storeId, itemIds, source, directive } = {}) => {
  const handoff = buildTemplateSetupHandoff({ storeId, itemIds, source, directive });
  if (!handoff.store_id || !handoff.item_ids.length) return null;
  try {
    localStorage.setItem(TEMPLATE_SETUP_HANDOFF_KEY, JSON.stringify(handoff));
    // The per-store marker is what lets the conversation resume if the user
    // closes the tab before the templates are created.
    localStorage.setItem(templateSetupActiveKey(handoff.store_id), '1');
  } catch (_) {
    return null;
  }
  return handoff;
};

/**
 * Stash the batch and open the Count page so the assistant takes over.
 * Returns false without navigating when the handoff could not be stashed —
 * the caller shows its own "your items are saved, but…" message.
 */
export const launchTemplateSetup = ({ storeId, itemIds, source, directive, from = 'item-import' } = {}) => {
  if (!stashTemplateSetupHandoff({ storeId, itemIds, source, directive })) return false;
  const href = new URL(templateSetupHref(from), window.location.origin).toString();
  try {
    // The native shell owns its own navigation stack; the browser assign below
    // stays as the fallback for the web app and for a shell that ignores it.
    if (window.ReactNativeWebView && typeof window.ReactNativeWebView.postMessage === 'function') {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'navigate',
        payload: { routeId: 'count', url: href },
      }));
    }
  } catch (_) { /* no-op */ }
  window.location.assign(href);
  return true;
};
